import { API_BASE_URL } from '../config';

async function loadHeicConvert() {
  const convert = await import('heic-convert/browser');
  return convert;
}

export const handleFileUpload = async (file, token, callbacks) => {
  const {
    onUploadStart,
    onUploadSuccess,
    onUploadError,
    onUploadComplete,
  } = callbacks;

  if (!file) {
    const error = new Error('No file selected. Please choose a file to upload.');
    onUploadError(error.message);
    return null;
  }

  onUploadStart();
  try {
    const formData = new FormData();
    const fileExtension = file.name.split('.').pop().toLowerCase();
    let mimeType = 'image/*';
    let fileWithMimeType;

    console.log("FILE EXTENSION:", fileExtension)

    // Handle PDF files directly without conversion
    if (fileExtension === 'pdf') {
      mimeType = 'application/pdf';
      fileWithMimeType = new File([file], file.name, { type: mimeType });
    }
    else if (fileExtension === 'heic' || fileExtension === 'heif') {
      // HEIC conversion can be problematic on Safari
      // Consider adding a check for Safari and providing an alternative or warning
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      if (isSafari) {
        console.warn('HEIC conversion may not be fully supported in Safari. Consider using a different image format.');
      }

      try {
        const fileBuffer = await file.arrayBuffer();
        const convert = await loadHeicConvert();
        const outputBuffer = await convert.default({ // Note: default is needed as convert is an ES module.
          buffer: new Uint8Array(fileBuffer),
          format: 'JPEG',
          quality: 0.8 // Reduced quality to improve performance
        });
        const jpegBlob = new Blob([outputBuffer], { type: 'image/jpeg' });
        fileWithMimeType = new File([jpegBlob], file.name.replace(/\.(heic|heif)$/i, '.jpg'), { type: 'image/jpeg' });
        mimeType = 'image/jpeg';
      } catch (conversionError) {
        console.error('Error converting HEIC:', conversionError);
        onUploadError('Failed to convert HEIC image. Please try a different format.');
        return null;
      }
    } else if (fileExtension === 'jpg' || fileExtension === 'jpeg') {
      // For JPEG images, we apply sharpening
      mimeType = 'image/jpeg';
      // Create a sharpened version of the image
      const sharpenImage = (file) => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = function(e) {
            const img = new Image();
            img.onload = function() {
              const canvas = document.createElement('canvas');
              const ctx = canvas.getContext('2d');
              canvas.width = img.width;
              canvas.height = img.height;

              ctx.drawImage(img, 0, 0, img.width, img.height);
              const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
              
              // Apply contrast adjustment (adjust the value as needed)
              const contrastedImageData = applyContrastToImages(imageData, 70);
              
              // Put the contrasted image data back on the canvas
              ctx.putImageData(contrastedImageData, 0, 0);
              
              // Convert the canvas to a Blob
              canvas.toBlob((blob) => {
                const sharpenedFile = new File([blob], file.name, { type: mimeType });
                resolve(sharpenedFile);
              }, mimeType, 0.8); // quality 0.8 seems to be a good balance.
            };
            img.src = e.target.result;
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      };

      try {
        fileWithMimeType = await sharpenImage(file);

      } catch (error) {
        console.error('Error sharpening image:', error);
        onUploadError('Failed to process the image. Please try again.');
        return null;
      }
    } else if (fileExtension === 'png') {
      mimeType = 'image/png';
      fileWithMimeType = new File([file], file.name, { type: mimeType });
    } else if (fileExtension === 'pdf') {
      mimeType = 'application/pdf';
      fileWithMimeType = new File([file], file.name, { type: mimeType });
    } else {
      onUploadError(`Unsupported file format: ${fileExtension}. Please use JPEG, PNG, HEIC, or PDF.`);
      return null;
    }

    formData.append('receipts', fileWithMimeType);

    const response = await fetch(`${API_BASE_URL}/upload_receipts`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      // First try to get the response as text
      const responseText = await response.text();
      
      // If we have a text response, use it as the error message
      const error = new Error(responseText.trim() || `Failed to upload receipt: ${response.status} ${response.statusText}`);
      onUploadError(error.message);
      return null;
    }

    const data = await response.json();
    console.log('Upload successful:', data);
    onUploadSuccess(data);

    return data;
  } catch (error) {
    console.error('Error processing file:', error);
    // Pass through the actual error message
    onUploadError(error.message || 'Failed to process the file. Please try again.');
    return null;  // Return null instead of throwing
  } finally {
    onUploadComplete();
  }
};

export const fetchReceipts = async (token) => {
  try {
    const response = await fetch(`${API_BASE_URL}/get_receipts`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch receipts');
    }

    const data = await response.json();
    
    if (data != null) {
      // Sort receipts from latest to oldest
      return data.sort((a, b) => {
        const dateA = a.date ? new Date(a.date) : new Date(0);
        const dateB = b.date ? new Date(b.date) : new Date(0);
        return dateB - dateA;
      });
    }
    return [];
  } catch (error) {
    console.error('Error fetching receipts:', error);
    throw error;
  }
};

// Adapted from https://github.com/jhuckaby/canvas-plus/blob/master/lib/filter/adjust.js
// Licensed under the MIT License by Copyright (c) 2017 Joseph Huckaby
// https://github.com/jhuckaby/canvas-plus/blob/master/LICENSE.md
const applyContrastToImages = (imgData, contrast) => {
    const rawValues = imgData.data;
    const con = Math.max(0, Math.min(255, contrast)) / 100 + 1; // Normalize contrast value between 0 and 255
    for (let i = 0; i < rawValues.length; i += 4) {   // r,g,b,a
        // Apply contrast to each color channel
        rawValues[i] = Math.floor(((rawValues[i] / 255 - 0.5) * con + 0.4) * 255);     // Red
        rawValues[i + 1] = Math.floor(((rawValues[i + 1] / 255 - 0.5) * con + 0.4) * 255); // Green
        rawValues[i + 2] = Math.floor(((rawValues[i + 2] / 255 - 0.5) * con + 0.4) * 255); // Blue
    }
    return imgData;
};

// Function to update a receipt field
export const updateReceiptField = async (token, receiptUUID, field, value) => {
  try {
    const response = await fetch(`${API_BASE_URL}/edit_receipt`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        receipt_uuid: receiptUUID,
        field,
        value,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to update receipt');
    }

    return await response.json();
  } catch (error) {
    console.error('Error updating receipt:', error);
    throw error;
  }
};

// Function to update a receipt item field
export const updateReceiptItemField = async (token, itemID, field, value) => {
  try {
    const response = await fetch(`${API_BASE_URL}/edit_receipt_item`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        item_id: itemID,
        field,
        value,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to update receipt item');
    }

    return await response.json();
  } catch (error) {
    console.error('Error updating receipt item:', error);
    throw error;
  }
};

// Utility function to get the effective value of a field (edited or original)
export const getEffectiveValue = (obj, fieldName) => {
  const editedField = `${fieldName}_edited`;
  return obj[editedField] || obj[fieldName];
};

// Utility function to check if a field has been edited
export const isFieldEdited = (obj, fieldName) => {
  const editedField = `${fieldName}_edited`;
  return obj[editedField] != null && obj[editedField] !== '';
};

// Function to fetch item categories
export const fetchItemCategories = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/item-categories`);
    if (!response.ok) {
      throw new Error('Failed to fetch categories');
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching categories:', error);
    throw error;
  }
};

// Helper function to get available detailed categories for a high level category
export const getDetailedCategories = (categories, highLevelCategory) => {
  if (!categories || !categories.categories) return [];
  
  const category = categories.categories.find(c => c.name === highLevelCategory);
  return category ? category.subcategories : [];
};

export const deleteReceiptItem = async (token, itemId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/delete_receipt_item?item_id=${itemId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to delete receipt item');
    }
  } catch (error) {
    console.error('Error deleting receipt item:', error);
    throw error;
  }
};

// Function to fetch a single receipt by UUID
export const fetchReceipt = async (token, receiptUUID) => {
  try {
    const response = await fetch(`${API_BASE_URL}/get_receipt?uuid=${receiptUUID}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch receipt');
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching receipt:', error);
    throw error;
  }
};

// Function to fetch receipt items
export const fetchReceiptItems = async (token, receiptUUID) => {
  try {
    const response = await fetch(`${API_BASE_URL}/get_receipt_items?receipt_id=${receiptUUID}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch receipt items');
    }

    return await response.json() || [];
  } catch (error) {
    console.error('Error fetching receipt items:', error);
    throw error;
  }
};

// Function to fetch receipt items with their tags
export const fetchReceiptItemsWithTags = async (token, receiptUUID) => {
  try {
    const items = await fetchReceiptItems(token, receiptUUID);
    
    // Fetch tags for each item
    const itemsWithTags = await Promise.all(
      items.map(async (item) => {
        try {
          const tagsResponse = await fetch(`${API_BASE_URL}/api/receipt-items/tags?item_id=${item.item_id}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          });
          
          if (tagsResponse.ok) {
            const tags = await tagsResponse.json();
            return { ...item, tags: tags || [] };
          } else {
            return { ...item, tags: [] };
          }
        } catch (error) {
          console.error(`Error fetching tags for item ${item.item_id}:`, error);
          return { ...item, tags: [] };
        }
      })
    );
    
    return itemsWithTags;
  } catch (error) {
    console.error('Error fetching receipt items with tags:', error);
    throw error;
  }
};

// Function to apply local updates to a receipt
export const applyLocalReceiptUpdate = (receipt, field, value) => {
  if (!receipt) return receipt;
  
  return {
    ...receipt,
    [`${field}_edited`]: value
  };
};

// Function to apply local updates to receipt items
export const applyLocalItemUpdate = (items, itemId, field, value) => {
  if (!items || !items.length) return items;
  
  return items.map(item => 
    item.item_id === itemId ? { ...item, [`${field}_edited`]: value } : item
  );
};

// Function to format a date for API submission
export const formatDateForAPI = (dateStr) => {
  if (!dateStr) return null;
  
  // Try to parse the date
  const date = parseDate(dateStr);
  if (!date) return null;
  
  // Format as YYYY-MM-DD
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  
  return `${year}-${month}-${day}`;
};

// Helper function to parse a date string in various formats
export const parseDate = (dateStr) => {
  if (!dateStr) return null;
  
  // If it's already in YYYY-MM-DD format
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(Date.UTC(year, month - 1, day));
  }
  
  // If it's in MM/DD/YYYY or MM/DD/YY format
  const parts = dateStr.split('/');
  if (parts.length === 3) {
    const [month, day, year] = parts.map(Number);
    if (!isNaN(month) && !isNaN(day) && !isNaN(year)) {
      // Always assume 2000s for two-digit years
      const fullYear = year < 100 ? 2000 + year : year;
      return new Date(Date.UTC(fullYear, month - 1, day));
    }
  }
  
  return null;
};

// Function to format a date for display
export const formatDate = (dateStr) => {
  const date = parseDate(dateStr);
  if (!date) return 'Date missing';
  
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  const year = date.getUTCFullYear();
  
  return `${month}/${day}/${year}`;
};